import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, Loader2, Settings2, Send, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('hw_token')}` });

const TYPE_LABELS = { guest: 'Ospiti', owner: 'Proprietari', partner: 'Partner', manager: 'Responsabili', unknown: 'Sconosciuti' };
const TYPE_ORDER = ['guest', 'owner', 'partner', 'manager', 'unknown'];
const LANGS = ['it', 'en', 'fr', 'de', 'es', 'nl', 'pl', 'pt'];

const csatClass = (avg) => {
  if (avg == null) return 'text-slate-300';
  if (avg >= 4.5) return 'text-emerald-600';
  if (avg >= 3.5) return 'text-amber-600';
  return 'text-rose-600';
};

const Cell = ({ cell }) => {
  if (!cell || !cell.n) return <span className="text-slate-300">—</span>;
  return (
    <span className={`font-bold ${csatClass(cell.avg)}`}>
      {cell.avg} <span className="text-[10px] font-normal text-slate-400">({cell.n})</span>
    </span>
  );
};

export default function CsFeedbackTab({ isAdmin }) {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [recentList, setRecentList] = useState([]);
  const [lowOnly, setLowOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = new Date(Date.now() - (days - 1) * 864e5).toISOString().slice(0, 10);
      const to = new Date().toISOString().slice(0, 10);
      const params = { date_from: from, date_to: to };
      const h = { headers: authHeaders() };
      const [s, m, r] = await Promise.all([
        axios.get(`${API}/cs-control/feedback/summary`, { params, ...h }),
        axios.get(`${API}/cs-control/feedback/matrix`, { params, ...h }),
        axios.get(`${API}/cs-control/feedback/recent`, { params: { low_only: lowOnly, limit: 50 }, ...h }),
      ]);
      setSummary(s.data);
      setRows(m.data.rows || []);
      setRecentList(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento feedback');
    }
    setLoading(false);
  }, [days, lowOnly]);

  useEffect(() => { load(); }, [load]);

  if (loading && !summary) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-500" /></div>;

  const bt = summary?.by_type || {};

  return (
    <div className="space-y-4" data-testid="cs-feedback-tab">
      <div className="flex flex-wrap items-center gap-2">
        {[7, 30, 90].map(d => (
          <button key={d} onClick={() => setDays(d)} data-testid={`cs-fb-days-${d}`}
            className={`px-3 py-1.5 rounded-full text-sm border ${days === d ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300'}`}>
            {d} giorni
          </button>
        ))}
        {isAdmin && (
          <Button size="sm" variant="outline" className="ml-auto" data-testid="cs-fb-settings-btn" onClick={() => setShowSettings(true)}>
            <Settings2 className="h-4 w-4 mr-1" /> Configurazione
          </Button>
        )}
      </div>

      {summary?.blocked_preview > 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          ⚠️ {summary.blocked_preview} invii bloccati dal preview-guard (in produzione partiranno normalmente)
        </p>
      )}

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase">Survey inviate</p>
          <p className="text-2xl font-bold text-slate-800" data-testid="cs-fb-sent">{summary?.sent ?? 0}</p>
          <p className="text-xs text-slate-400">
            {summary?.scheduled > 0 && `${summary.scheduled} in coda`}
            {summary?.scheduled > 0 && summary?.by_delivery?.email > 0 && ' · '}
            {summary?.by_delivery?.email > 0 && `${summary.by_delivery.email} via email 📧`}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase">Risposte</p>
          <p className="text-2xl font-bold text-slate-800" data-testid="cs-fb-responses">
            {summary?.responses ?? 0} <span className="text-sm text-slate-400">({summary?.response_rate_pct ?? '—'}%)</span>
          </p>
          {summary?.reminders_sent > 0 && <p className="text-xs text-slate-400">{summary.reminders_sent} promemoria inviati 🔔</p>}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase">Gradimento globale</p>
          <p className={`text-2xl font-bold ${csatClass(summary?.csat_avg)}`} data-testid="cs-fb-csat">
            {summary?.csat_avg ?? '—'} <span className="text-sm text-slate-400">/ 5</span>
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase">Distribuzione</p>
          <div className="flex items-end gap-1 mt-1 h-8">
            {[1, 2, 3, 4, 5].map(v => {
              const n = summary?.distribution?.[v] || 0;
              const max = Math.max(...[1, 2, 3, 4, 5].map(x => summary?.distribution?.[x] || 0), 1);
              return (
                <div key={v} className="flex flex-col items-center flex-1" title={`${v}★: ${n}`}>
                  <div className={`w-full rounded-t ${v >= 4 ? 'bg-emerald-400' : v === 3 ? 'bg-amber-400' : 'bg-rose-400'}`}
                    style={{ height: `${Math.max((n / max) * 100, 4)}%` }} />
                  <span className="text-[9px] text-slate-400">{v}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Gradimento per tipologia */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {TYPE_ORDER.map(t => (
          <div key={t} className="bg-white rounded-xl border border-slate-200 p-3 text-center" data-testid={`cs-fb-type-${t}`}>
            <p className="text-[11px] text-slate-500 uppercase">{TYPE_LABELS[t]}</p>
            <p className={`text-xl font-bold ${csatClass(bt[t]?.avg)}`}>{bt[t]?.avg ?? '—'}</p>
            <p className="text-[10px] text-slate-400">{bt[t]?.responses || 0} voti</p>
          </div>
        ))}
      </div>

      {/* Matrice operatore × tipologia */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <Star className="h-4 w-4 text-amber-500" />
          <h3 className="text-sm font-semibold text-slate-700">Chi è più bravo con chi — gradimento per operatore e tipologia chiamante</h3>
        </div>
        {rows.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-400">Nessun feedback nel periodo. I voti compariranno qui appena i clienti rispondono alle survey WhatsApp.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cs-fb-matrix">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Operatore</th>
                  <th className="text-center px-3 py-2">Globale</th>
                  {TYPE_ORDER.map(t => <th key={t} className="text-center px-3 py-2">{TYPE_LABELS[t]}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`cs-fb-matrix-row-${i}`}>
                    <td className="px-4 py-2 font-medium text-slate-700">{r.operator} <span className="text-xs text-slate-400">({r.responses})</span></td>
                    <td className="text-center px-3 py-2"><Cell cell={{ n: r.responses, avg: r.avg }} /></td>
                    {TYPE_ORDER.map(t => <td key={t} className="text-center px-3 py-2"><Cell cell={r.by_type?.[t]} /></td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Feedback recenti */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">Ultimi feedback ricevuti</h3>
          <label className="flex items-center gap-1.5 text-xs text-slate-500">
            <input type="checkbox" checked={lowOnly} onChange={e => setLowOnly(e.target.checked)} data-testid="cs-fb-low-filter" />
            Solo negativi (≤2)
          </label>
        </div>
        {recentList.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-400">Nessun feedback ancora.</p>
        ) : (
          <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {recentList.map((f, i) => (
              <div key={i} className="px-4 py-2.5 flex items-start gap-3" data-testid={`cs-fb-recent-${i}`}>
                <span className={`text-sm font-bold px-2 py-0.5 rounded-full ${f.rating >= 4 ? 'bg-emerald-100 text-emerald-700' : f.rating === 3 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
                  {f.rating}★
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700">
                    <span className="font-medium">{f.caller_name || f.phone}</span>
                    <span className="text-xs text-slate-400"> · {TYPE_LABELS[f.caller_type] || f.caller_type} · op. {f.operator_name || '—'}</span>
                  </p>
                  {f.comment && <p className="text-xs text-slate-500 italic mt-0.5">"{f.comment}"</p>}
                </div>
                <span className="text-[10px] text-slate-400 whitespace-nowrap">
                  {f.rated_at ? new Date(f.rated_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) : ''}
                </span>
                {isAdmin && (
                  <button data-testid={`cs-fb-delete-${i}`} title="Elimina questo voto (es. voto di test)"
                    onClick={async () => {
                      if (!window.confirm(`Eliminare il voto ${f.rating}★ di ${f.caller_name || f.phone}? L'operazione è definitiva e aggiorna le statistiche.`)) return;
                      try {
                        await axios.delete(`${API}/cs-control/feedback/${f.id}`, { headers: authHeaders() });
                        toast.success('Voto eliminato ✅');
                        load();
                      } catch (e) { toast.error(e?.response?.data?.detail || 'Errore eliminazione'); }
                    }}
                    className="text-slate-300 hover:text-rose-600 transition-colors p-0.5 shrink-0">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showSettings && <FeedbackSettingsDialog onClose={() => { setShowSettings(false); load(); }} />}
    </div>
  );
}

function FeedbackSettingsDialog({ onClose }) {
  const [s, setS] = useState(null);
  const [webhooks, setWebhooks] = useState({});
  const [webhooksTicket, setWebhooksTicket] = useState({});
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/cs-control/feedback/settings`, { headers: authHeaders() });
        setS(r.data);
        const wh = {}, wht = {};
        LANGS.forEach(l => {
          wh[l] = { webhook_url: r.data.webhooks?.[l]?.webhook_url || '', secret: r.data.webhooks?.[l]?.secret || '', has_secret: r.data.webhooks?.[l]?.has_secret };
          wht[l] = { webhook_url: r.data.webhooks_ticket?.[l]?.webhook_url || '', secret: r.data.webhooks_ticket?.[l]?.secret || '', has_secret: r.data.webhooks_ticket?.[l]?.has_secret };
        });
        setWebhooks(wh);
        setWebhooksTicket(wht);
      } catch (_) { toast.error('Errore caricamento impostazioni'); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/cs-control/feedback/settings`, { ...s, webhooks, webhooks_ticket: webhooksTicket }, { headers: authHeaders() });
      const wh = r.data?.webhooks || {};
      const withSecret = Object.keys(wh).filter(l => wh[l]?.has_secret).map(l => l.toUpperCase());
      toast.success(`Impostazioni salvate ✅ Webhook chiamate: ${Object.keys(wh).length} — con secret proprio: ${withSecret.join(', ') || 'nessuno (si usa il secret di sistema)'}`, { duration: 9000 });
      onClose();
    } catch (e) { toast.error(`Errore salvataggio (HTTP ${e?.response?.status || 'rete'}): ${e?.response?.data?.detail || e.message}`, { duration: 12000 }); }
    setSaving(false);
  };

  const testSend = async () => {
    if (!testPhone.trim()) { toast.error("Inserisci un numero o un'email"); return; }
    try {
      const r = await axios.post(`${API}/cs-control/feedback/test`, { phone: testPhone.trim() }, { headers: authHeaders() });
      if (r.data.status === 'sent') toast.success(`Survey di test inviata ✅ (lingua ${(r.data.language || 'it').toUpperCase()})`);
      else toast.error(`Esito: ${r.data.status} — ${r.data.note || 'nessun dettaglio'}`, { duration: 12000 });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore test'); }
  };

  if (!s) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cs-fb-settings-dialog">
        <DialogHeader><DialogTitle>Configurazione feedback post-chiamata</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={!!s.enabled} onChange={e => setS({ ...s, enabled: e.target.checked })} data-testid="cs-fb-enabled" />
            Sistema attivo (invia survey WhatsApp dopo le chiamate)
          </label>
          <p className="text-xs text-slate-500 -mt-2">📧 Chi chiama da numero fisso riceve automaticamente la survey via email (se l'indirizzo è noto in rubrica: ospite, proprietario, partner o responsabile). Nessuna configurazione necessaria.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div><Label className="text-xs">Durata min. (sec)</Label>
              <Input type="number" value={s.min_talk_seconds} onChange={e => setS({ ...s, min_talk_seconds: parseInt(e.target.value) || 0 })} data-testid="cs-fb-min-talk" /></div>
            <div><Label className="text-xs">Ritardo invio (min)</Label>
              <Input type="number" value={s.delay_minutes} onChange={e => setS({ ...s, delay_minutes: parseInt(e.target.value) || 0 })} data-testid="cs-fb-delay" /></div>
            <div><Label className="text-xs">Non ripetere per (gg)</Label>
              <Input type="number" value={s.dedup_days} onChange={e => setS({ ...s, dedup_days: parseInt(e.target.value) || 0 })} data-testid="cs-fb-dedup" /></div>
            <div><Label className="text-xs">Fascia oraria</Label>
              <div className="flex items-center gap-1">
                <Input type="number" value={s.send_hour_start} onChange={e => setS({ ...s, send_hour_start: parseInt(e.target.value) || 9 })} className="w-16" />
                <span className="text-slate-400">-</span>
                <Input type="number" value={s.send_hour_end} onChange={e => setS({ ...s, send_hour_end: parseInt(e.target.value) || 20 })} className="w-16" />
              </div></div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={s.reminder_enabled !== false} onChange={e => setS({ ...s, reminder_enabled: e.target.checked })} data-testid="cs-fb-reminder-enabled" />
              🔔 Promemoria a chi non risponde, dopo
            </label>
            <Input type="number" value={s.reminder_after_hours ?? 24} onChange={e => setS({ ...s, reminder_after_hours: parseInt(e.target.value) || 24 })}
              data-testid="cs-fb-reminder-hours" className="w-20 h-8" />
            <span className="text-sm text-slate-500">ore (una sola volta, in fascia oraria)</span>
          </div>
          <div><Label className="text-xs">Link recensione Google (mostrato ai voti 4-5)</Label>
            <Input value={s.google_review_url || ''} onChange={e => setS({ ...s, google_review_url: e.target.value })}
              placeholder="https://g.page/r/..." data-testid="cs-fb-google-url" /></div>
          <div><Label className="text-xs">Email alert voti negativi (≤2)</Label>
            <Input value={s.alert_email || ''} onChange={e => setS({ ...s, alert_email: e.target.value })} data-testid="cs-fb-alert-email" /></div>

          <div className="border-t border-slate-100 pt-3">
            <p className="text-sm font-semibold text-slate-700 mb-1">Webhook Spoki per lingua — feedback CHIAMATE</p>
            <p className="text-xs text-slate-500 mb-2">Crea su Spoki un template "feedback chiamata" con pulsante URL <b>hwsmart.it/f/</b> + campo dinamico <b>FEEDBACK_TOKEN</b> e un'automazione webhook "invio immediato" per lingua (come i Guest Video). La lingua è riconosciuta dal prefisso del numero; se il webhook della lingua manca si usa EN, poi IT. <b>Secret</b>: i secret salvati restano visibili qui sotto (solo per gli amministratori); campo vuoto = viene usato il secret Spoki di sistema, lo stesso dei Guest Video.</p>
            <div className="space-y-1.5">
              {LANGS.map(l => (
                <div key={l} className="grid grid-cols-[36px_1fr_180px] gap-2 items-center">
                  <span className="text-xs font-bold uppercase text-slate-500">{l}</span>
                  <Input placeholder="URL webhook automazione Spoki" value={webhooks[l]?.webhook_url || ''}
                    onChange={e => setWebhooks({ ...webhooks, [l]: { ...webhooks[l], webhook_url: e.target.value } })}
                    data-testid={`cs-fb-wh-url-${l}`} className="h-8 text-xs" />
                  <Input placeholder="Secret (vuoto = usa secret di sistema)"
                    value={webhooks[l]?.secret || ''}
                    onChange={e => setWebhooks({ ...webhooks, [l]: { ...webhooks[l], secret: e.target.value } })}
                    data-testid={`cs-fb-wh-secret-${l}`} className="h-8 text-xs" />
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-100 pt-3">
            <p className="text-sm font-semibold text-slate-700 mb-1">Webhook Spoki per lingua — feedback TICKET (alla chiusura)</p>
            <p className="text-xs text-slate-500 mb-2">Template separato con testo tipo "Com'è stata gestita la tua richiesta?" — inviato a responsabile/proprietario quando il ticket viene chiuso. Se non configurato, i ticket non generano survey.</p>
            <div className="space-y-1.5">
              {LANGS.map(l => (
                <div key={l} className="grid grid-cols-[36px_1fr_180px] gap-2 items-center">
                  <span className="text-xs font-bold uppercase text-slate-500">{l}</span>
                  <Input placeholder="URL webhook automazione Spoki (ticket)" value={webhooksTicket[l]?.webhook_url || ''}
                    onChange={e => setWebhooksTicket({ ...webhooksTicket, [l]: { ...webhooksTicket[l], webhook_url: e.target.value } })}
                    data-testid={`cs-fb-wht-url-${l}`} className="h-8 text-xs" />
                  <Input placeholder="Secret (vuoto = usa secret di sistema)"
                    value={webhooksTicket[l]?.secret || ''}
                    onChange={e => setWebhooksTicket({ ...webhooksTicket, [l]: { ...webhooksTicket[l], secret: e.target.value } })}
                    data-testid={`cs-fb-wht-secret-${l}`} className="h-8 text-xs" />
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-100 pt-3 flex items-end gap-2">
            <div className="flex-1"><Label className="text-xs">Test: invia survey a un numero (WhatsApp) o a un'email</Label>
              <Input value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="+39... oppure nome@email.it" data-testid="cs-fb-test-phone" /></div>
            <Button variant="outline" size="sm" onClick={testSend} data-testid="cs-fb-test-btn"><Send className="h-4 w-4 mr-1" /> Invia test</Button>
          </div>

          <Button onClick={save} disabled={saving} data-testid="cs-fb-save-settings" className="w-full">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salva impostazioni'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
